import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AutomationJob } from '@/hooks/useAutomationJobs';
import { Loader2, AlertTriangle, CheckCircle2, Clock, Pause, Play, Square } from 'lucide-react';

interface MergeJobsModalProps {
  jobs: AutomationJob[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMerge: (jobIds: string[], newName: string) => Promise<void>;
}

export const MergeJobsModal = ({ jobs, open, onOpenChange, onMerge }: MergeJobsModalProps) => {
  const [mergedName, setMergedName] = useState('');
  const [merging, setMerging] = useState(false);

  const stats = useMemo(() => {
    const totalTarget = jobs.reduce((sum, j) => sum + j.total_images_target, 0);
    const totalGenerated = jobs.reduce((sum, j) => sum + (j.success_count || j.images_generated), 0);
    const runningJobs = jobs.filter(j => j.status === 'running');
    const uniquePrompts = new Set(jobs.map(j => j.base_prompt)).size;
    const uniqueModels = new Set(jobs.flatMap(j => j.models_to_use)).size;
    
    return {
      totalTarget,
      totalGenerated,
      runningJobs,
      hasDifferentPrompts: uniquePrompts > 1,
      hasDifferentModels: uniqueModels > jobs[0]?.models_to_use.length,
    };
  }, [jobs]);

  const canMerge = jobs.length >= 2 && stats.runningJobs.length === 0 && mergedName.trim();

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="h-3 w-3 text-green-500" />;
      case 'pending':
        return <Clock className="h-3 w-3 text-yellow-500" />;
      case 'paused':
        return <Pause className="h-3 w-3 text-orange-500" />;
      case 'running':
        return <Play className="h-3 w-3 text-blue-500" />;
      case 'stopped':
        return <Square className="h-3 w-3 text-gray-500" />;
      default:
        return null;
    }
  };

  const handleMerge = async () => {
    if (!canMerge) return;

    setMerging(true);
    try {
      await onMerge(jobs.map(j => j.id), mergedName.trim());
      onOpenChange(false);
      setMergedName('');
    } finally {
      setMerging(false);
    }
  };

  // Reset name when modal opens
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setMergedName('');
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg bg-background/95 backdrop-blur-md border-border/50">
        <DialogHeader>
          <DialogTitle>Merge {jobs.length} Jobs</DialogTitle>
          <DialogDescription>
            Combine selected jobs into a single job. Images will be reassigned to the new merged job.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Jobs to merge */}
          <div className="space-y-2">
            <Label>Jobs to merge:</Label>
            <ScrollArea className="h-40 rounded-md border p-2">
              <div className="space-y-2">
                {jobs.map((job) => (
                  <div 
                    key={job.id} 
                    className="flex items-center justify-between p-2 rounded bg-muted/50"
                  >
                    <div className="flex items-center gap-2">
                      {getStatusIcon(job.status)}
                      <span className="text-sm font-medium truncate max-w-[200px]">
                        {job.job_name}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        {job.success_count || job.images_generated}/{job.total_images_target}
                      </span>
                      <Badge variant="outline" className="text-xs">
                        {job.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>

          {/* Combined stats */}
          <div className="grid grid-cols-2 gap-4 p-3 rounded-lg bg-muted/30">
            <div>
              <p className="text-xs text-muted-foreground">Combined Target</p>
              <p className="text-lg font-semibold">{stats.totalTarget} images</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Combined Generated</p>
              <p className="text-lg font-semibold">{stats.totalGenerated} images</p>
            </div>
          </div>

          {/* Warnings */}
          {stats.runningJobs.length > 0 && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Cannot merge while jobs are running. Pause {stats.runningJobs.length} running job(s) first.
              </AlertDescription>
            </Alert>
          )}

          {stats.hasDifferentPrompts && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Jobs have different prompts. The merged job will use the prompt from the first selected job.
              </AlertDescription>
            </Alert>
          )}

          {stats.hasDifferentModels && (
            <Alert>
              <AlertDescription>
                Jobs use different models. The merged job will use models from the first selected job.
              </AlertDescription>
            </Alert>
          )}

          {/* New job name */}
          <div className="space-y-2">
            <Label htmlFor="mergedName">Merged Job Name</Label>
            <Input
              id="mergedName"
              value={mergedName}
              onChange={(e) => setMergedName(e.target.value)}
              placeholder="Enter name for merged job"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={merging}>
            Cancel
          </Button>
          <Button onClick={handleMerge} disabled={!canMerge || merging}>
            {merging ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Merging...
              </>
            ) : (
              `Merge ${jobs.length} Jobs`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
